import React, { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Box,
  Button,
  MenuItem,
  Table as MuiTable,
  Select,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'

import { ReactComponent as Settings } from '../../../assets/icons/settings.svg'
import useTranslations from '../../../i18n/useTranslations'
import EnhancedTableHead from './EnhancedTableHeader'
import EnhancedTableToolbar from './EnhancedTableToolbar'
import {
  REPORTS_CONFIG_TABLE_HEADER_ORDER,
  REPORTS_CONFIG_TABLE_HEAD_CELLS,
  REPORT_TYPES,
} from '../../../common/constants'
import { useStyles } from '../styles'
import ModalLayout from '../../../ui/modals/layout'
import { Link } from 'react-router-dom/cjs/react-router-dom'
import { useUserContext } from '../../../auth/UserProvider'

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map(el => el[0])
}

//TODO: REFACTOR TO AVOID THE NECESSITY OF RECEIVING PAGING PARAMS
//THIS SHOULD BE OPTIONAL
const DEFAULT_PAGING_PARAMS = {
  page: 0,
  rowsPerPage: 10,
  totalRows: 28,
}

const TableReportConfig = ({
  onChangeTableParams,
  defaultSortType = 'desc',
  rows,
  tableParams = DEFAULT_PAGING_PARAMS,
  rowsPerPageOptions = [10, 25, 50],
  setReportToConfig,
  reportSubscriptions,
  updateSub,
}) => {
  const t = useTranslations()
  const classes = useStyles()
  const user = useUserContext()
  const [deleteFile, setDeleteFile] = useState(null)
  const [reportToSub, setReportToSub] = useState()
  const { page, rowsPerPage, totalRows } = tableParams

  const showLabelDisplayedRows = () => {
    if (!totalRows) return null
    const indexInitRow = (page + 1) * rowsPerPage - rowsPerPage + 1
    const indexLastRow = Math.min(indexInitRow + rowsPerPage - 1, totalRows)
    return `${indexInitRow}-${indexLastRow} de ${totalRows}`
  }

  const handleRequestSort = (event, sortColumn) => {
    const getOppositeSortType = sortType =>
      sortType === 'asc' ? 'desc' : 'asc'

    onChangeTableParams({
      ...tableParams,
      sortColumn,
      sortType:
        sortColumn === tableParams.sortColumn
          ? getOppositeSortType(tableParams.sortType)
          : defaultSortType,
    })
  }

  // eslint-disable-next-line
  const handleChangePage = (event, newPage) => {
    onChangeTableParams({ ...tableParams, page: newPage })
  }

  const handleChangeRowsPerPage = event => {
    onChangeTableParams({
      ...tableParams,
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    })
  }

  const checkIsSub = reportCode => {
    let findSub = reportSubscriptions?.find(report =>
      report.reports.some(report => report.report_code === reportCode),
    )
    let isCheck = findSub?.reports?.find(
      report => report?.report_code === reportCode,
    )

    return isCheck?.subscription_enabled ? true : false
  }

  return (
    <>
      <ModalLayout
        onClose={() => setReportToSub(null)}
        isOpen={reportToSub}
        actions={
          <>
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => setReportToSub(null)}
            >
              {t('action:cancel')}
            </Button>
            <Button
              type="submit"
              size="small"
              onClick={() => {
                updateSub({
                  reportId: reportToSub?.report_code,
                  enabled: !checkIsSub(reportToSub?.report_code),
                  webUserCode: user.webUserCode,
                })
                setReportToSub(null)
              }}
            >
              {t('action:confirm')}
            </Button>
          </>
        }
        content={
          <>
            <Typography variant="body2">
              {t(
                checkIsSub(reportToSub?.report_code)
                  ? 'reports:confirm-unsub-description'
                  : 'reports:confirm-sub-description',
              )}
            </Typography>
          </>
        }
        title={t(
          checkIsSub(reportToSub?.report_code)
            ? 'reports:confirm-unsub-title'
            : 'reports:confirm-sub-title',
        )}
      />
      <TableContainer>
        <MuiTable aria-labelledby="tableTitle" aria-label="enhanced table">
          <EnhancedTableHead
            headCells={REPORTS_CONFIG_TABLE_HEAD_CELLS}
            order={tableParams.sortType}
            orderBy={tableParams.sortColumn}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {stableSort(
              rows,
              getComparator(tableParams.sortType, tableParams.sortColumn),
            ).map((row, index) => {
              const id = row?.report_code
              const isChecked = checkIsSub(id)
              return (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={id}
                    className={clsx(row?.details?.severity)}
                  >
                    {REPORTS_CONFIG_TABLE_HEADER_ORDER?.map((key, index) =>
                      index === 0 ? (
                        <TableCell key={key} className="main">
                          {row[key]}
                        </TableCell>
                      ) : key === 'subscription' ? (
                        <TableCell key={key}>
                          <Switch
                            name="suscription"
                            checked={isChecked}
                            onChange={e => {
                              setReportToSub(row)
                            }}
                          />
                        </TableCell>
                      ) : key === 'type' ? (
                        <TableCell key={key}>
                          <Select
                            labelId="reportType"
                            id="reportType"
                            label={
                              <Typography variant="h4bold">
                                {t('noun:clientCenterCode')}
                              </Typography>
                            }
                            name="clientCenterCode"
                            className={classes.reportTypes}
                          >
                            {REPORT_TYPES.map(({ id, text }) => (
                              <MenuItem key={id} value={id}>
                                {text}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                      ) : key === 'settings' ? (
                        <TableCell key={key}>
                          <Settings
                            onClick={() => {
                              setReportToConfig(row)
                              setReportToSub(null)
                            }}
                          />
                        </TableCell>
                      ) : (
                        <TableCell key={key}>{row[key]}</TableCell>
                      ),
                    )}
                  </TableRow>
                </React.Fragment>
              )
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {true ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          py={2}
          px={1}
        ></Box>
      ) : null}
    </>
  )
}

TableReportConfig.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      info: PropTypes.oneOfType([
        PropTypes.shape({
          numero_colegiado: PropTypes.string,
          nombre: PropTypes.string,
          apellidos: PropTypes.string,
          modalidades: PropTypes.string,
          farmacia: PropTypes.string,
          telefono: PropTypes.string,
          correo_electronico: PropTypes.string,
        }),
        PropTypes.shape({
          nombre: PropTypes.string,
          telefono: PropTypes.string,
          correo_electronico: PropTypes.string,
          estado: PropTypes.string,
        }),
        PropTypes.shape({
          year: PropTypes.string,
          day: PropTypes.string,
          reason: PropTypes.string,
          type: PropTypes.string,
          locality: PropTypes.string,
        }),
      ]),
    }),
  ),
  tableParams: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    totalRows: PropTypes.number,
    sortType: PropTypes.string,
    sortColumn: PropTypes.string,
  }),
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.string),
  onChangeTableParams: PropTypes.func,
  defaultSortType: PropTypes.oneOf(['desc', 'asc']),
  handleSubmitDelete: PropTypes.func,
  handleSubmitGetDetail: PropTypes.func,
}

export default TableReportConfig
